import qs from "qs"
import { PopoverClose } from "@radix-ui/react-popover"
import { Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { LoaderCircleIcon, PencilIcon, PlusIcon, TrashIcon } from "lucide-react"
import { Outlet, useLoaderData, useFetcher, useRouteLoaderData } from "react-router"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"

import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"

import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"

import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"

import { hasPermission } from "@/machines/Authmachine"

import type { RootLoader } from "@/routes/root"
import type { TagType } from "@/utils/types"

const routeId = "tagtypes-builder"
const sessionKey = "tagtypes-filter-storage"

const tagRestrictionOptions = [
  { value: "freeTextMultiple", label: "Free Text (Multiple allowed)" },
  { value: "freeTextSingle", label: "Free Text (Singular)" },
  { value: "fixedMultiple", label: "Fixed (Multiple allowed)" },
  { value: "fixedSingle", label: "Fixed (Singular)" },
  { value: "autoTag", label: "Auto Tag" },
  { value: "instanceAutoTag", label: "Instance Auto Tag" },
]

async function loader() {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>

  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  const projects = getProjectsByOrganisation(loadedOrganisation)
  const loadedProject =
    filters.parsed?.project == null
      ? projects[0]
      : (projects.find((prj) => prj === filters.parsed?.project) ?? projects[0])

  const tagTypes = await fetchAPI<TagType[]>(`/api/UnityViewer/TagType/${loadedOrganisation}/${loadedProject}`).then(
    handleResponse,
  )

  return { tagTypes, loadedOrganisation, projects, loadedProject }
}

async function action({ request }: { request: Request }) {
  const formData = await request.formData()
  const { action, organisation, project } = Object.fromEntries(formData) as Record<string, string>

  let errorMessage: string | undefined

  if (organisation == null) throw new Error("Missing organisation")
  if (project == null) throw new Error("Missing project")

  switch (action) {
    case "CREATE": {
      const tagRestriction = formData.get("tagRestriction") as string
      const name = formData.get("name") as string | null

      if (name == null || tagRestriction == null) {
        errorMessage = "Missing Tag Type Name"
        break
      }

      try {
        await fetchAPI(`/api/UnityViewer/tagtype/${organisation}/${project}`, {
          method: "POST",
          body: { name, tagRestriction },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = "Error Adding Tag Type"
      }

      break
    }

    default:
      throw new Error("Unknown action")
  }

  return { action, errorMessage }
}

async function mainAction({ request }: { request: Request }) {
  const formData = await request.formData()
  const { action, name, tagRestriction, organisation, project } = Object.fromEntries(formData) as Record<string, string>

  if (organisation == null) throw new Error("Missing organisation")
  if (project == null) throw new Error("Missing project")

  let errorMessage: string | undefined

  switch (action) {
    case "DELETE": {
      if (name == null || tagRestriction == null) {
        errorMessage = "Missing Tag Type Name/Restriction"
        break
      }

      try {
        await fetchAPI(`/api/UnityViewer/tagtype/${organisation}/${project}/${name}`, { method: "DELETE" }).then(
          throwIfNotOk,
        )
      } catch (error) {
        console.error(error)
        errorMessage = "Error Deleting Tag Type"
      }
      break
    }
    case "EDIT": {
      if (name == null || tagRestriction == null) {
        errorMessage = "Missing Tag Type Name/Restriction"
        break
      }

      try {
        await fetchAPI(`/api/UnityViewer/tagtype/${organisation}/${project}`, {
          method: "POST",
          body: { name, tagRestriction },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = "Error Updating Tag Type"
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action, errorMessage }
}

export function TagTypes() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)

  const onBeforeSubmit = useCallback<OnBeforeSubmit>((data, reason) => {
    if (reason.type === "field" && reason.name === "organisation") data.delete("project")
  }, [])

  return (
    <Fragment>
      {rootData != null && data != null && (
        <div className="flex items-center gap-4">
          {rootData.organisations.length > 1 && (
            <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
              <FiltersWrapper.Select
                name="organisation"
                label="Organisation"
                defaultValue={data.loadedOrganisation}
                items={rootData.organisations}
              />
              {data.projects.length > 1 && (
                <FiltersWrapper.Select
                  name="project"
                  label="Project"
                  defaultValue={data.loadedProject}
                  items={data.projects}
                />
              )}
            </FiltersWrapper>
          )}
          <div className="flex-1" />
          <TagTypeAddNew />
        </div>
      )}
      <Outlet />
    </Fragment>
  )
}

function Main() {
  const data = useLoaderData<typeof loader>()

  return (
    <div className="h-full overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead></TableHead>
            <TableHead className="text-left">Tag Name</TableHead>
            <TableHead className="text-left">Tag Restriction</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.tagTypes.length > 0 ? (
            data.tagTypes.map((element, index) => (
              <TableRow key={element.name} className="text-sm">
                <TableCell>{index + 1}.</TableCell>
                <TableCell>{element.name}</TableCell>
                <TableCell>
                  <TagTypeRestriction tagRestriction={element.tagRestriction} />
                </TableCell>
                <TableCell className="space-x-2">
                  <TagTypeEdit {...element} />
                  <TagTypeDelete {...element} />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={12} className="py-4 text-center text-gray-500">
                No data found for this organisation.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Outlet />
    </div>
  )
}

function TagTypeRestriction({ tagRestriction }: Pick<TagType, "tagRestriction">) {
  const displayLabel = useMemo(
    () => tagRestrictionOptions.find((option) => option.value === tagRestriction)?.label ?? tagRestriction,
    [tagRestriction],
  )

  return displayLabel
}

function TagTypeAddNew() {
  const [open, setOpen] = useState(false)

  const fetcher = useFetcher<typeof action>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  useEffect(() => {
    if (fetcher.data?.action !== "CREATE" || fetcher.data.errorMessage != null) return
    setOpen(false)
  }, [fetcher.data])

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon /> Add New
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Tag Type</DialogTitle>
        </DialogHeader>
        <fetcher.Form method="POST" className="space-y-4">
          <input type="hidden" name="action" value="CREATE" />
          <input type="hidden" name="organisation" value={data.loadedOrganisation} />
          <input type="hidden" name="project" value={data.loadedProject} />
          <div className="flex-1" />
          <div>
            <label className="text-sm font-medium">Tag Name</label>
            <Input required type="text" name="name" placeholder="Tag Name" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Tag Restriction Type</label>
            <Select name="tagRestriction" defaultValue={tagRestrictionOptions[0].value ?? ""}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select restriction type" />
              </SelectTrigger>
              <SelectContent>
                {tagRestrictionOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-start gap-4">
            {fetcher.state !== "idle" ? (
              <span className="flex items-center justify-start gap-1 pr-4">
                <LoaderCircleIcon className="size-4 animate-spin" /> Loading
              </span>
            ) : (
              fetcher.data?.errorMessage != null && (
                <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
              )
            )}
            <div className="flex-1" />
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button type="submit">Submit</Button>
          </div>
        </fetcher.Form>
      </DialogContent>
    </Dialog>
  )
}

function TagTypeDelete(tagType: TagType) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Delete Tag Type">
          <TrashIcon className="text-red-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="left" align="start" className="max-w-sm">
        <p className="text-sm text-pretty">
          &quot;<span className="font-semibold">{tagType.name}</span>&quot; will be deleted from the Tag Type List. Are
          you sure?
        </p>
        <TagTypeDeleteForm {...tagType} />
      </PopoverContent>
    </Popover>
  )
}

function TagTypeDeleteForm({ name, tagRestriction }: TagType) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null) return null

  return (
    <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="name" value={name} />
      <input type="hidden" name="tagRestriction" value={tagRestriction} />
      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
        </span>
      ) : (
        fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost">
        Confirm
      </Button>
    </fetcher.Form>
  )
}

function TagTypeEdit(tagType: TagType) {
  const [open, setOpen] = useState(false)
  const closeDialog = useCallback(() => setOpen(false), [])

  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Edit Tag Type">
          <PencilIcon />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Tag Type</DialogTitle>
        </DialogHeader>
        <TagTypeEditForm {...tagType} onSuccess={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function TagTypeEditForm({ name, tagRestriction, onSuccess }: TagType & { onSuccess: () => void }) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  useEffect(() => {
    if (fetcher.data?.action !== "EDIT" || fetcher.data.errorMessage != null) return
    onSuccess()
  }, [onSuccess, fetcher.data])

  if (data == null) return null

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="EDIT" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="name" value={name} />
      <div className="flex-1" />
      <div>
        <label className="text-sm font-medium">Tag Name</label>
        <Input type="text" disabled value={name} />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">Tag Restriction Type</label>
        <Select name="tagRestriction" defaultValue={tagRestriction}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select restriction type" />
          </SelectTrigger>
          <SelectContent>
            {tagRestrictionOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Loading
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button type="submit">Submit</Button>
      </div>
    </fetcher.Form>
  )
}

TagTypes.loader = loader
TagTypes.action = action
TagTypes.Main = Main
TagTypes.routeId = routeId

Main.action = mainAction
